import { usersKeys } from 'api/queryKeys';
import { editUser, lockUserAccess, unlockUserAccess, updateAccount } from 'api/user';
import { Flex, Line } from 'components';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useMeQuery } from 'hooks/queries/useMeQuery';
import { useUserQuery } from 'hooks/queries/useUserQuery';
import { useUserFunctionsQuery } from 'hooks/queries/useUserFunctionsQuery';
import { BackButton } from 'components/BackButton';
import { CustomTab } from 'components/Tabs/CustomTab';
import { CustomTabList } from 'components/Tabs/CustomTabList';
import { CustomTabPanel } from 'components/Tabs/CustomTabPanel';
import { CustomTabs } from 'components/Tabs/CustomTabs';
import { H1, P } from 'components/Typography/Typography';
import { Button } from 'components/_form';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { Accordion } from 'react-accessible-accordion';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IProfileForm } from 'types/forms/profile';
import { IUserForm } from 'types/forms/user';
import { IUser } from 'types/user';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { displayTranslatedField } from 'utilities/localizedString';
import { isUserAccountActive } from 'utilities/userAccount';
import { AreaPermissionsForm } from './components/AreaPermissions/AreaPermissionsForm';
import { AvatarForm } from './components/Avatar/AvatarForm';
import { MailNotificationsForm } from './components/MailNotifications/MailNotificationsForm';
import { OtherForm } from './components/Other/OtherForm';
import { BasicInformationForm } from './components/Profile/BasicInformationForm';
import { EmailAddressForm } from './components/Profile/EmailAddressForm';
import { PasswordForm } from './components/Profile/PasswordForm/PasswordForm';
import { UsernameForm } from './components/Profile/UsernameForm';
import { StyledAccordionItem, Wrapper } from './ProfileView.styled';

export const ProfileView = () => {
  const { data: mainCompany } = useMainCompanyQuery();
  const [userData, setUserData] = useState<IUser>();

  const { id, defaultTabIndex } = useParams();
  const { data: meData } = useMeQuery();
  const { data: userDataById } = useUserQuery(id ?? null);
  const { data: userFunctionsData = [] } = useUserFunctionsQuery();
  const userDataFromQuery = id ? userDataById : meData;

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [accountActiveSwitchKey, setAccountActiveSwitchKey] = useState(0);

  const { register, handleSubmit, reset, control } = useForm();

  const companyOptions = React.useMemo(() => {
    if (!mainCompany) return [];
    const list = mainCompany.id
      ? [{ id: mainCompany.id, full_name: mainCompany.full_name, short_name: mainCompany.short_name }]
      : [];
    const partners = mainCompany.partner_companies || [];
    const all = [...list, ...partners];
    return all
      .filter((c) => c.id != null)
      .map((c) => ({ label: c.full_name || c.short_name || String(c.id), value: String(c.id!) }));
  }, [mainCompany]);

  const functionOptions = React.useMemo(
    () =>
      userFunctionsData.map((fn) => ({
        label: displayTranslatedField(fn, 'name'),
        value: String(fn.id)
      })),
    [userFunctionsData]
  );

  const { data: areasData = [] } = useAreasQuery();
  const areaOptions = React.useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );

  useEffect(() => {
    if (!userDataFromQuery) return;
    setUserData(userDataFromQuery);
    const { role, username, profile, company_id, functions, areas } =
      userDataFromQuery;
    const functionsSafe = functions ?? [];
    const areasSafe = areas ?? [];
    if (profile) {
      const { photo, ...restData } = profile;
      reset({
        ...restData,
        role,
        username,
        company_id,
        function_ids: functionsSafe.map((fn) => String(fn.id)),
        area_ids: areasSafe.map((a) => String(a.id))
      });
      return;
    }
    reset({
      role,
      username,
      company_id,
      function_ids: functionsSafe.map((fn) => String(fn.id)),
      area_ids: areasSafe.map((a) => String(a.id))
    });
  }, [userDataFromQuery, reset]);

  const handleUpdateProfile = async (
    data: Partial<IProfileForm['profile']> &
      Partial<Omit<IUserForm['user'], 'profile'>>
  ) => {
    try {
      if (!userData?.id) return;

      const { email, profile, ...restUserData } = userData;
      const { id: profileId, photo, ...restProfile } = profile || {
        id: null,
        photo: null
      };
      const { username, role, function_ids, area_ids, ...restData } = data;

      const payload = {
        user: {
          ...restData,
          username: username || restUserData.username,
          role: role ?? restUserData.role,
          function_ids: Array.isArray(function_ids) ? function_ids.map((id) => Number(id)) : undefined,
          area_ids: Array.isArray(area_ids) ? area_ids.map((id) => Number(id)) : undefined,
          profile_attributes: { ...restProfile, ...restData }
        }
      };

      const { data: newUserData } = id
        ? await editUser(id, payload)
        : await updateAccount(payload);
      setUserData(newUserData);

      toast.success(t('common.success'));
    } catch (e) {
      console.error(e);
      toast.error(t('anErrorOccurred'));
    }
  };

  const handleAccountActiveToggle = useCallback(
    async (nextActive: boolean) => {
      if (!id || !userData?.id) return;
      const current = isUserAccountActive(userData);
      if (nextActive === current) return;

      const ok = window.confirm(
        nextActive
          ? (t('profile.confirmUnlockUser') as string)
          : (t('profile.confirmLockUser') as string)
      );
      if (!ok) {
        setAccountActiveSwitchKey((k) => k + 1);
        return;
      }

      try {
        const { data: newUserData } = nextActive
          ? await unlockUserAccess(id)
          : await lockUserAccess(id);
        setUserData(newUserData);
        const companyId = String(mainCompany?.id ?? '');
        if (companyId) {
          await queryClient.invalidateQueries({ queryKey: usersKeys.detail(companyId, id) });
          await queryClient.invalidateQueries({ queryKey: usersKeys.list(companyId) });
        }
        toast.success(t('common.success'));
      } catch (e) {
        console.error(e);
        setAccountActiveSwitchKey((k) => k + 1);
        toast.error(t('anErrorOccurred'));
      }
    },
    [id, userData, mainCompany?.id, queryClient, t]
  );

  const handleUpdateProfilePhoto = (photo: Blob) => {
    handleUpdateProfile({ photo });
  };

  const handleSubmitUpdateProfile = handleSubmit((data) => {
    handleUpdateProfile(data);
  });

  return (
    <Wrapper flexDirection="column">
      <Flex width="100%">
        <BackButton />

        <H1 m={2} variant={['h3', 'h2']} textTransform="uppercase">
          {(() => {
            const name = [userData?.profile?.first_name, userData?.profile?.last_name].filter(Boolean).join(' ') || userData?.email || '';
            if (id) {
              return `${t('administration.employeesAndUsers')} - ${name}`;
            }
            return name ? `${t('profile.myProfile')} - ${name}` : t('profile.myProfile');
          })()}
        </H1>
      </Flex>
      <Line />
      <Flex minHeight="400px" flexWrap="wrap">
        <Flex
          px={3}
          width={['100%', '30%']}
          flexDirection="column"
          justifyContent="space-between"
        >
          <Flex width="100%" alignItems="center" flexDirection="column">
            <AvatarForm
              handleSubmit={handleUpdateProfilePhoto}
              avatar={userData?.profile?.photo}
            />
          </Flex>
          <Flex flexDirection="column" py={3}>
            <P variant="body">
              {t('profile.lastLogin')}:{' '}
              {userData?.current_sign_in_at_formatted || t('profile.sessionDateUnknown')}
            </P>
            <P mb={2} variant="body">
              {t('profile.lastActivity')}:{' '}
              {userData?.last_activity_at_formatted || t('profile.sessionDateUnknown')}
            </P>

            {id && (
              <Button variant="redWhite" bordered>
                {t('profile.deleteUser')}
              </Button>
            )}
          </Flex>
        </Flex>
        <Flex width={['100%', '70%']}>
          <CustomTabs
            defaultIndex={parseInt(defaultTabIndex || '0')}
            selectedTabClassName="is-selected"
          >
            <CustomTabList>
              <Flex flexWrap="wrap">
                <CustomTab>{t('profile.profile')}</CustomTab>
                <CustomTab>{t('profile.areaPermissions')}</CustomTab>
                <CustomTab>{t('profile.mailNotifications')}</CustomTab>
                <CustomTab>{t('common.other')}</CustomTab>
              </Flex>
            </CustomTabList>
            <CustomTabPanel>
              <Accordion allowMultipleExpanded allowZeroExpanded>
                <StyledAccordionItem>
                  <BasicInformationForm
                    id={id}
                    register={register}
                    onSubmit={handleSubmitUpdateProfile}
                    control={control}
                    companyOptions={companyOptions}
                    functionOptions={functionOptions}
                    areaOptions={areaOptions}
                    accountActive={isUserAccountActive(userData)}
                    accountActiveSwitchKey={accountActiveSwitchKey}
                    onAccountActiveChange={handleAccountActiveToggle}
                  />
                </StyledAccordionItem>
                <EmailAddressForm />
                <StyledAccordionItem>
                  <UsernameForm
                    register={register}
                    onSubmit={handleSubmitUpdateProfile}
                  />
                </StyledAccordionItem>
                <StyledAccordionItem>
                  <PasswordForm />
                </StyledAccordionItem>
              </Accordion>
            </CustomTabPanel>
            <CustomTabPanel>
              <AreaPermissionsForm
                user={userData}
                routeUserId={id}
                companyId={mainCompany?.id ?? ''}
                onUserUpdated={setUserData}
              />
            </CustomTabPanel>
            <CustomTabPanel>
              <MailNotificationsForm />
            </CustomTabPanel>
            <CustomTabPanel>
              <OtherForm id={id} />
            </CustomTabPanel>
          </CustomTabs>
        </Flex>
      </Flex>
    </Wrapper>
  );
};
